import { Platform, PlatformCatalogItem } from '@ignite/types';
import { useQuery } from '@tanstack/react-query';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { z } from 'zod';
import { apiClient } from '../../lib/auth';

const platformCacheVersion = 1;
const platformCachePrefix = 'ignite.workbench.platform-context';

const PlatformCache = z.object({
  version: z.literal(platformCacheVersion),
  platforms: z.array(PlatformCatalogItem),
  current_platform: Platform.nullable(),
});

type PlatformCache = z.infer<typeof PlatformCache>;
type PlatformContextStatus = 'loading' | 'ready' | 'stale' | 'empty';

export type PlatformSwitchGuard = {
  dirty: boolean;
  pending: boolean;
  discard: () => void;
};

type PlatformContextValue = {
  platforms: PlatformCatalogItem[];
  currentPlatform: Platform | null;
  currentPlatformItem: PlatformCatalogItem | null;
  status: PlatformContextStatus;
  setCurrentPlatform: (platform: Platform) => void;
  platformSwitchGuard: PlatformSwitchGuard | null;
  registerPlatformSwitchGuard: (guard: PlatformSwitchGuard) => () => void;
};

const PlatformContext = createContext<PlatformContextValue | null>(null);

export function PlatformProvider({
  userScope,
  children,
}: {
  userScope: string;
  children: ReactNode;
}) {
  const storageKey = useMemo(() => buildPlatformStorageKey(userScope), [userScope]);
  const cached = useMemo(() => readPlatformCache(storageKey), [storageKey]);
  const [selectedPlatform, setSelectedPlatform] = useState<Platform | null>(
    cached?.current_platform ?? null,
  );
  const [platformSwitchGuard, setPlatformSwitchGuard] = useState<PlatformSwitchGuard | null>(null);
  const platformQuery = useQuery({
    queryKey: ['platform-catalog'],
    queryFn: () => apiClient.platforms(),
  });

  const remotePlatforms = useMemo(
    () => platformQuery.data?.platforms.filter((platform) => platform.is_active) ?? null,
    [platformQuery.data],
  );
  const platforms = remotePlatforms ?? cached?.platforms ?? [];
  const currentPlatform = resolveCurrentPlatform(platforms, selectedPlatform);
  const currentPlatformItem =
    platforms.find((platform) => platform.code === currentPlatform) ?? null;

  useEffect(() => {
    if (!platformQuery.isSuccess || remotePlatforms === null) return;
    writePlatformCache(storageKey, {
      version: platformCacheVersion,
      platforms: remotePlatforms,
      current_platform: currentPlatform,
    });
  }, [currentPlatform, platformQuery.isSuccess, remotePlatforms, storageKey]);

  const setCurrentPlatform = useCallback(
    (platform: Platform) => {
      if (!platforms.some((item) => item.code === platform)) return;
      setSelectedPlatform(platform);
      writePlatformCache(storageKey, {
        version: platformCacheVersion,
        platforms,
        current_platform: platform,
      });
    },
    [platforms, storageKey],
  );

  const registerPlatformSwitchGuard = useCallback((guard: PlatformSwitchGuard) => {
    setPlatformSwitchGuard(guard);
    return () => {
      setPlatformSwitchGuard((current) => (current === guard ? null : current));
    };
  }, []);

  let status: PlatformContextStatus;
  if (platformQuery.isPending && platforms.length === 0) status = 'loading';
  else if (platformQuery.isError && platforms.length > 0) status = 'stale';
  else if (platforms.length === 0) status = 'empty';
  else status = 'ready';

  const value = useMemo<PlatformContextValue>(
    () => ({
      platforms,
      currentPlatform,
      currentPlatformItem,
      status,
      setCurrentPlatform,
      platformSwitchGuard,
      registerPlatformSwitchGuard,
    }),
    [
      currentPlatform,
      currentPlatformItem,
      platformSwitchGuard,
      platforms,
      registerPlatformSwitchGuard,
      setCurrentPlatform,
      status,
    ],
  );

  return <PlatformContext.Provider value={value}>{children}</PlatformContext.Provider>;
}

export function usePlatformContext(): PlatformContextValue {
  const value = useContext(PlatformContext);
  if (!value) throw new Error('usePlatformContext must be used within PlatformProvider');
  return value;
}

export function resolveCurrentPlatform(
  platforms: PlatformCatalogItem[],
  selectedPlatform: Platform | null,
): Platform | null {
  if (selectedPlatform && platforms.some((platform) => platform.code === selectedPlatform)) {
    return selectedPlatform;
  }
  return platforms[0]?.code ?? null;
}

function buildPlatformStorageKey(userScope: string): string {
  return `${platformCachePrefix}.${encodeURIComponent(userScope)}`;
}

function readPlatformCache(storageKey: string): PlatformCache | null {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed = PlatformCache.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

function writePlatformCache(storageKey: string, cache: PlatformCache): void {
  try {
    localStorage.setItem(storageKey, JSON.stringify(cache));
  } catch {
    // 存储不可用时仍保留当前会话内的平台上下文。
  }
}
